import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Alert, Divider, Stack, Switch,
  FormControlLabel, CircularProgress, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BlockIcon from '@mui/icons-material/Block';
import { technicianAccessAPI } from '../../services/api';

/**
 * Gestion del acceso de un tecnico a la app: alta con codigo y PIN temporal,
 * QR para escanear, envio por WhatsApp y revocacion inmediata.
 */
export default function TechnicianAccessPanel({ technician, onChanged }) {
  const [access, setAccess] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [pin, setPin] = useState(null);      // solo visible al generarlo
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [pinDialog, setPinDialog] = useState(false);
  const [manualPin, setManualPin] = useState('');

  // Muestra el detalle del backend, y si no hubo respuesta (fallo de red o de
  // serializacion) lo dice en vez de dejar un mensaje generico que no orienta.
  const describe = (e, fallback) =>
    e.response?.data?.detail
    || (e.response ? `${fallback} (HTTP ${e.response.status})` : `${fallback} ${e.message}`);

  const load = useCallback(async () => {
    try {
      const res = await technicianAccessAPI.get(technician.id);
      setAccess(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.status === 401
        ? 'Tu sesion ha caducado. Vuelve a entrar en la aplicacion.'
        : describe(e, 'No se ha podido cargar el estado de acceso.'));
    }
  }, [technician.id]);

  useEffect(() => { load(); }, [load]);

  // El QR exige token, asi que se descarga como blob y se pinta desde object URL
  useEffect(() => {
    let revoked = null;
    if (access?.login_code) {
      technicianAccessAPI.qrBlob(technician.id)
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoked = url;
          setQrUrl(url);
        })
        .catch(() => setQrUrl(null));
    } else {
      setQrUrl(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [access?.login_code, technician.id]);

  const grant = async (manualPin) => {
    setBusy(true);
    // Solo se manda un PIN si de verdad es un texto: si el boton se enganchara
    // directo a onClick, React colaria aqui el evento y axios no sabria
    // serializarlo, fallando sin respuesta del servidor.
    const pinValue = typeof manualPin === 'string' && manualPin.trim() ? manualPin.trim() : null;
    try {
      const res = await technicianAccessAPI.grant(
        technician.id, pinValue ? { pin: pinValue } : {});
      setAccess(res.data);
      setPin(res.data.pin);
      setPinDialog(false);
      setManualPin('');
      setError('');
      if (onChanged) onChanged();
    } catch (e) {
      setError(describe(e, 'No se ha podido dar de alta el acceso.'));
    } finally {
      setBusy(false);
    }
  };

  const setFlag = async (field, value) => {
    setBusy(true);
    try {
      const res = await technicianAccessAPI.update(technician.id, { [field]: value });
      setAccess({ ...res.data, pin: null });
      if (onChanged) onChanged();
    } catch (e) {
      setError(describe(e, 'No se ha podido actualizar.'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await technicianAccessAPI.revoke(technician.id);
      setPin(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(describe(e, 'No se ha podido revocar.'));
    } finally {
      setBusy(false);
    }
  };

  const copyAccess = () => {
    const text = [
      `Acceso a la app: ${access.access_url}`,
      `Codigo: ${access.login_code}`,
      pin ? `PIN temporal: ${pin}` : null,
    ].filter(Boolean).join('\n');
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Si la carga falla hay que decirlo: antes se quedaba girando el spinner para
  // siempre y no habia forma de saber que pasaba.
  if (!access) {
    return (
      <Box sx={{ p: 2 }}>
        {error
          ? <Alert severity="error" action={
              <Button size="small" onClick={load}>Reintentar</Button>
            }>{error}</Alert>
          : <CircularProgress size={20} />}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        Acceso a la app de tecnicos
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {!access.login_code || !access.app_access_enabled ? (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            Este tecnico todavia no puede entrar. Al darle acceso se genera un codigo
            y un PIN temporal que tendra que cambiar la primera vez.
          </Typography>
          <Button variant="contained" startIcon={<VpnKeyIcon />}
            onClick={() => grant()} disabled={busy}>
            {access.login_code ? 'Reactivar acceso' : 'Dar acceso a la app'}
          </Button>
        </Box>
      ) : (
        <Box>
          {/* Credenciales siempre visibles: es lo que hay que dictarle al tecnico */}
          <Box sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'primary.light',
                     borderRadius: 1, backgroundColor: '#e3f2fd' }}>
            <Typography variant="caption" color="textSecondary">
              Codigo de entrada del tecnico
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 2, mb: 0.5 }}>
              {access.login_code}
            </Typography>
            {pin ? (
              <>
                <Typography variant="caption" color="textSecondary">PIN</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 4 }}>
                  {pin}
                </Typography>
                <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setPin(null)}>
                  Apunta el PIN antes de cerrar: no se puede volver a consultar.
                  Si se pierde, genera otro con "PIN nuevo".
                </Alert>
              </>
            ) : (
              <Alert severity={access.last_login ? 'info' : 'warning'} sx={{ mt: 1 }}>
                {access.last_login
                  ? 'El PIN no se puede consultar: se guarda cifrado. Si el tecnico lo ha '
                    + 'perdido, pulsa "PIN nuevo" o "Poner un PIN concreto".'
                  : 'Este tecnico todavia no ha entrado nunca. Si no tiene el PIN, pulsa '
                    + '"Poner un PIN concreto" y dictaselo junto con el codigo de arriba.'}
              </Alert>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Chip size="small" color="success" label="Acceso activo" />
            {access.last_login ? (
              <Chip size="small" variant="outlined"
                label={`Ultimo acceso: ${access.last_login.slice(0, 16).replace('T', ' ')}`} />
            ) : (
              <Chip size="small" color="warning" label="Todavia no ha entrado nunca" />
            )}
            {access.must_change_pin && (
              <Chip size="small" variant="outlined" label="Cambiara el PIN al entrar" />
            )}
          </Stack>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {qrUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <Box component="img" src={qrUrl} alt="QR de acceso"
                  sx={{ width: 150, height: 150, border: '1px solid #ddd', p: 0.5 }} />
                <Typography variant="caption" display="block" color="textSecondary">
                  Escanear con la camara
                </Typography>
              </Box>
            )}

            <Box sx={{ flexGrow: 1, minWidth: 240 }}>
              <Typography variant="caption" color="textSecondary">Enlace de acceso</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                {access.access_url}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                {access.whatsapp_url ? (
                  <Button size="small" variant="contained" color="success"
                    startIcon={<WhatsAppIcon />} href={access.whatsapp_url}
                    target="_blank" rel="noopener noreferrer">
                    Enviar por WhatsApp
                  </Button>
                ) : (
                  <Tooltip title="El tecnico no tiene telefono en la ficha">
                    <span>
                      <Button size="small" variant="contained" color="success"
                        startIcon={<WhatsAppIcon />} disabled>
                        Enviar por WhatsApp
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={copied ? 'Copiado' : 'Copiar acceso'}>
                  <IconButton size="small" onClick={copyAccess}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <FormControlLabel
                control={
                  <Switch checked={!!access.is_team_leader} disabled={busy}
                    onChange={(e) => setFlag('is_team_leader', e.target.checked)} />
                }
                label={
                  <Box>
                    <Typography variant="body2">Jefe de equipo</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Puede crear tareas y asignarlas a otros tecnicos
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<VpnKeyIcon />}
              onClick={() => grant()} disabled={busy}>
              PIN nuevo
            </Button>
            <Button size="small" onClick={() => setPinDialog(true)} disabled={busy}>
              Poner un PIN concreto
            </Button>
            <Button size="small" color="error" startIcon={<BlockIcon />}
              onClick={revoke} disabled={busy}>
              Revocar acceso
            </Button>
          </Stack>
        </Box>
      )}

      <Dialog open={pinDialog} onClose={() => setPinDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Poner un PIN concreto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Util para dictarselo por telefono. El tecnico tendra que cambiarlo al entrar.
          </Typography>
          <TextField autoFocus fullWidth label="PIN (minimo 6 digitos)" value={manualPin}
            onChange={(e) => setManualPin(e.target.value.replace(/\D/g, ''))}
            inputProps={{ inputMode: 'numeric', maxLength: 12,
                          style: { fontSize: 22, letterSpacing: 4, textAlign: 'center' } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialog(false)}>Cancelar</Button>
          <Button variant="contained" disabled={manualPin.length < 6 || busy}
            onClick={() => grant(manualPin)}>
            Guardar PIN
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
